// src/components/corkboard/corkTokens.js
//
// Design tokens for the Cork Board page.
//
// Same rule as `manuscript/structureTokens.js`, and for the same reason: the
// prototype this page was built from carried its own palette — a purple of
// #4B2E92, a gold of #C9A24B and a cork brown of #C08A56 — and none of those
// are the app's colours. `purple` and `gold` here are
// `theme.palette.primary.main` and `theme.palette.secondary.main`, and
// `goldLight` / `goldDeep` are the two stops either side of that gold in the
// `containedSecondary` gradient in `theme.js`. Everything else is a tint or
// shade of those two.
//
// **The cork surface is the interesting case.** A corkboard has to read as
// cork, and the prototype got there with a brown belonging to no palette at
// all. Rather than import it, `cork` and `corkDeep` are the app's gold taken
// down in lightness and saturation until it reads as cork — so the board looks
// like a corkboard and still looks like this app. The frame is the same shade
// darker again. If the brand gold ever changes, these move with it; a copied
// #C08A56 would not.
//
// Kept as plain values rather than reached for through `useTheme()` for the
// reason structureTokens.js gives: most are shades the MUI palette does not
// carry, and mixing two sources for one page's colours is how a palette drifts.
//
// Fonts are not defined here — Source Serif 4, IBM Plex Mono and Inter are
// already loaded in `public/index.html`. FONT below just names the stacks, and
// is re-exported from structureTokens so the two pages cannot drift apart.

export { FONT, eyebrowSx } from '../manuscript/structureTokens';

// The responsive tiers and the floating-panel width now live in
// structureTokens, alongside FONT, so the Structure page can use them without
// importing the Cork Board's own tokens. Re-exported here because every
// corkboard component reads them from this file.
export { BP, FLOATING_PANEL_W } from '../manuscript/structureTokens';
export const DOCKED_PANEL_W = 320;

export const CB = {
  purple: '#413B8D',      // theme.palette.primary.main
  purpleDeep: '#231F4D',  // the masthead ground, as on the Structure page
  purpleMid: '#5A52B0',
  purpleSoft: '#8F89C4',
  lavender: '#F4F2FB',
  lineSoft: '#E3E0F2',

  gold: '#D4AF37',        // theme.palette.secondary.main
  goldLight: '#F5D76E',   // containedSecondary gradient, light stop
  goldDeep: '#C49A00',    // containedSecondary gradient, deep stop

  // The board itself: theme secondary, desaturated and darkened into cork.
  cork: '#A98D4E',
  corkDeep: '#8C7340',
  corkFrame: '#6E5A32',

  black: '#14121B',
  gray: '#6B6A78',
  white: '#FFFFFF',
  lineLt: 'rgba(255,255,255,0.14)',

  danger: '#B3401F',      // the destructive red already used by the editor panels
};

// One colour per element type. These are **not** new: they are the same values
// as `TYPE_DOT` in `editor/StoryElementsPanel.jsx`, so an actor is the same
// lilac on the board as it is in the editor's sidebar. Changing one without the
// other is how the two views stop looking like one feature.
export const TYPE_COLOR = {
  chapter: '#8E86C9',
  scene: CB.gold,
  actor: '#A99BE8',
  artifact: '#E6A0B5',
};

// The card face for each type: a pale tint of its dot colour, with ink dark
// enough to stay readable on it, and the pin head a solid of the same family.
// Index cards, not sticky notes — the prototype's paper whites read as cork
// board stationery, which is the part of its look worth keeping.
export const TYPE_CARD = {
  scene: { bg: '#FBF5E4', ink: '#5A4717', edge: CB.goldDeep },
  actor: { bg: '#F2EFFC', ink: '#2E2660', edge: CB.purple },
  artifact: { bg: '#FBEEF2', ink: '#6B2438', edge: '#C2647F' },
};

// Anything whose type the board does not know — a node type added to the
// backend before the board learns about it. Rendered rather than dropped.
export const FALLBACK_CARD = { bg: CB.lavender, ink: CB.black, edge: CB.purpleSoft };

export const cardFor = (type) => TYPE_CARD[type] || FALLBACK_CARD;

export const TYPE_LABEL = {
  scene: 'Scene',
  actor: 'Actor',
  artifact: 'Artifact',
  chapter: 'Chapter',
};

// The string between two pinned cards. Red thread is the one piece of the
// prototype's palette that is iconography rather than branding — a corkboard
// investigation board is red string — so it stays, tuned to sit against the
// cork above rather than the prototype's brown.
export const STRING_COLOR = '#B03A2E';
